import { ref, readonly } from 'vue'
import type { Note } from '../types/note'
import { apiClient } from '../services/apiClient'

// 全局狀態
const notes = ref<Note[]>([])
const currentNote = ref<Note | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const isSaving = ref(false)
const lastSavedTime = ref<string | null>(null)
let autoSaveTimer: number | null = null

export function useGlobalNotes() {
  const loadNotes = async () => {
    loading.value = true
    try {
      error.value = null
      notes.value = await apiClient.getAllNotes()
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load notes'
    } finally {
      loading.value = false
    }
  }

  const loadNotesByFrame = async (frameId: number) => {
    try {
      error.value = null
      const frameNotes = await apiClient.getNotesByFrame(frameId)
      return frameNotes
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load frame notes'
      throw e
    }
  }

  const loadNotesByVerse = async (versionCode: string, bookId: number, chapterNumber: number) => {
    try {
      error.value = null
      const verseNotes = await apiClient.getNotesByVerse(versionCode, bookId, chapterNumber)
      return verseNotes
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load verse notes'
      throw e
    }
  }

  const createNote = async (noteData: {
    frameId?: number
    versionCode: string
    bookId: number
    chapterNumber: number
    verseStart?: number
    content?: string
  }): Promise<Note> => {
    try {
      error.value = null
      const note = await apiClient.createNote(noteData)
      notes.value = [...notes.value, note]
      return note
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to create note'
      throw e
    }
  }

  const updateNote = async (noteId: number, content: string): Promise<Note> => {
    try {
      isSaving.value = true
      error.value = null
      const updated = await apiClient.updateNote(noteId, { content })
      // 更新全局和當前筆記
      const idx = notes.value.findIndex(n => n.id === noteId)
      if (idx !== -1) {
        notes.value = [
          ...notes.value.slice(0, idx),
          updated,
          ...notes.value.slice(idx + 1),
        ]
      }
      if (currentNote.value?.id === noteId) {
        currentNote.value = updated
      }
      lastSavedTime.value = new Date().toLocaleTimeString('zh-TW', {
        hour: '2-digit',
        minute: '2-digit',
      })
      return updated
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to update note'
      throw e
    } finally {
      isSaving.value = false
    }
  }

  const deleteNote = async (noteId: number) => {
    try {
      error.value = null
      await apiClient.deleteNote(noteId)
      notes.value = notes.value.filter(n => n.id !== noteId)
      if (currentNote.value?.id === noteId) {
        currentNote.value = null
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to delete note'
      throw e
    }
  }

  const selectNote = (note: Note) => {
    currentNote.value = note
    startAutoSave()
  }

  const startAutoSave = () => {
    if (autoSaveTimer) clearInterval(autoSaveTimer)

    let lastContent = currentNote.value?.content || ''
    let isDirty = false

    autoSaveTimer = window.setInterval(() => {
      if (!currentNote.value) return

      if (currentNote.value.content !== lastContent) {
        isDirty = true
      }

      if (isDirty && currentNote.value) {
        updateNote(currentNote.value.id, currentNote.value.content)
          .then(() => {
            lastContent = currentNote.value?.content || ''
            isDirty = false
          })
          .catch((e) => {
            console.error('Auto-save failed:', e)
          })
      }
    }, 30000) // 30 秒
  }

  const stopAutoSave = () => {
    if (autoSaveTimer) {
      clearInterval(autoSaveTimer)
      autoSaveTimer = null
    }
  }

  return {
    notes: readonly(notes),
    currentNote: readonly(currentNote),
    loading: readonly(loading),
    error: readonly(error),
    isSaving: readonly(isSaving),
    lastSavedTime: readonly(lastSavedTime),
    loadNotes,
    loadNotesByFrame,
    loadNotesByVerse,
    createNote,
    updateNote,
    deleteNote,
    selectNote,
    startAutoSave,
    stopAutoSave,
  }
}
